import { logError } from '../../utils/logging';
import { removeDanglingRelationships } from './docxPackageCleanup';

/* global Office, Word */

/**
 * Utility class for exporting Word documents
 */
export class DocumentExporter {
  /**
   * Checks if Office.js is available
   */
  private static isOfficeAvailable(): boolean {
    return typeof Office !== 'undefined' && typeof Office.context !== 'undefined';
  }

  /**
   * Extracts the filename from a document URL or file path.
   * Word on Windows may return backslash paths or full paths in the title property.
   */
  private static extractFilenameFromPath(pathOrUrl: string): string {
    let normalized = pathOrUrl.trim();

    if (normalized.toLowerCase().startsWith('file://')) {
      normalized = normalized.slice('file://'.length);
      // file:///C:/path on Windows — drop leading slash before drive letter
      if (/^\/[a-zA-Z]:/.test(normalized)) {
        normalized = normalized.slice(1);
      }
    }

    const segments = normalized.split(/[/\\]/).filter((segment) => segment.length > 0);
    const basename = segments.length > 0 ? segments[segments.length - 1] : normalized;

    try {
      return decodeURIComponent(basename);
    } catch {
      return basename;
    }
  }

  /**
   * OLE / unset Word dates show up as epoch or 1899 — treat those as missing.
   */
  private static toIsoDate(value: Date | string | number | null | undefined): string | null {
    if (value == null || value === '') return null;
    const date = value instanceof Date ? value : new Date(value);
    const ms = date.getTime();
    if (!Number.isFinite(ms)) return null;
    if (date.getUTCFullYear() < 1980) return null;
    return date.toISOString();
  }

  /**
   * Basename + timestamps from the open document (never the local folder path).
   * lastSaveTime is the primary snapshot id; createdAt is the unsaved fallback.
   */
  static async getDocumentIdentity(): Promise<{
    name: string;
    lastSaveTime: string | null;
    createdAt: string | null;
  }> {
    const fallbackName = 'document.docx';
    if (!this.isOfficeAvailable()) {
      return { name: fallbackName, lastSaveTime: null, createdAt: null };
    }

    const nameFromUrl = Office.context.document.url
      ? this.extractFilenameFromPath(Office.context.document.url)
      : null;

    try {
      return await Word.run(async (context) => {
        const properties = context.document.properties;
        properties.load(['title', 'lastSaveTime', 'creationDate']);
        await context.sync();

        let name = nameFromUrl;
        if (!name) {
          const title = properties.title || 'Untitled Document';
          name = DocumentExporter.extractFilenameFromPath(title);
          if (!name.toLowerCase().endsWith('.docx')) {
            name = `${name}.docx`;
          }
        }

        return {
          name,
          lastSaveTime: DocumentExporter.toIsoDate(properties.lastSaveTime),
          createdAt: DocumentExporter.toIsoDate(properties.creationDate),
        };
      });
    } catch (error) {
      logError('Failed to get document identity', error);
      return {
        name: nameFromUrl || fallbackName,
        lastSaveTime: null,
        createdAt: null,
      };
    }
  }

  /**
   * Gets the document filename from the Word document
   * @returns The document filename (with extension) or a default name
   */
  static async getDocumentName(): Promise<string> {
    if (!this.isOfficeAvailable()) {
      // Return default name when Office.js is not available (testing mode)
      return 'document.docx';
    }

    try {
      // Try to get the document URL which contains the filename
      if (Office.context.document.url) {
        return this.extractFilenameFromPath(Office.context.document.url);
      }

      // Fallback: Use Word API to get document properties
      return await Word.run(async (context) => {
        const properties = context.document.properties;
        properties.load('title');
        await context.sync();
        
        // Use title if available, otherwise use default
        const title = properties.title || 'Untitled Document';
        const filename = DocumentExporter.extractFilenameFromPath(title);
        
        // Ensure it has .docx extension
        if (!filename.toLowerCase().endsWith('.docx')) {
          return `${filename}.docx`;
        }
        
        return filename;
      });
    } catch (error) {
      logError('Failed to get document name', error);
      return 'document.docx';
    }
  }

  /**
   * Gets the document filename without extension
   * @returns The document filename without extension
   */
  static async getDocumentNameWithoutExtension(): Promise<string> {
    const filename = await this.getDocumentName();
    return filename.replace(/\.docx$/i, '');
  }

  /**
   * Exports the Word document as a File/Blob for upload
   * @param filename The desired filename for the exported file
   * @returns A File object containing the document data
   */
  /**
   * Word's own PDF of the open document, rendered by Word on this computer (docs/word-pdf-rendering).
   *
   * Desktop Word only (Windows and Mac): there Word renders locally and nothing is sent anywhere.
   * On Word on the web, or when Word refuses, this returns null and the server renders the document
   * itself (silent fallback, logged).
   */
  static async getDocumentAsPdf(): Promise<Blob | null> {
    if (typeof Office === 'undefined' || !Office.context?.document) {
      return null;
    }
    const platform = Office.context.platform;
    if (platform !== Office.PlatformType.PC && platform !== Office.PlatformType.Mac) {
      console.info('[DocumentExporter] Word PDF skipped: not desktop Word', platform);
      return null;
    }
    try {
      const bytes = await new Promise<Uint8Array>((resolve, reject) => {
        Office.context.document.getFileAsync(
          Office.FileType.Pdf,
          { sliceSize: 65536 },
          (result: Office.AsyncResult<Office.File>) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
              reject(new Error(result.error?.message || 'Word could not export a PDF'));
              return;
            }
            const file = result.value;
            const slices: Uint8Array[] = [];
            const next = (index: number) => {
              file.getSliceAsync(index, (sliceResult: Office.AsyncResult<Office.FileSlice>) => {
                if (sliceResult.status === Office.AsyncResultStatus.Failed) {
                  file.closeAsync();
                  reject(new Error(sliceResult.error?.message || 'Failed to read a PDF slice'));
                  return;
                }
                slices.push(new Uint8Array(sliceResult.value.data));
                if (index + 1 < file.sliceCount) {
                  next(index + 1);
                  return;
                }
                file.closeAsync();
                const total = slices.reduce((n, slice) => n + slice.length, 0);
                const combined = new Uint8Array(total);
                let offset = 0;
                for (const slice of slices) {
                  combined.set(slice, offset);
                  offset += slice.length;
                }
                resolve(combined);
              });
            };
            next(0);
          },
        );
      });
      const isPdf = bytes.length > 4 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;
      if (!isPdf) {
        console.warn('[DocumentExporter] Word PDF skipped: not a PDF');
        return null;
      }
      return new Blob([bytes as Uint8Array<ArrayBuffer>], { type: 'application/pdf' });
    } catch (error) {
      console.warn('[DocumentExporter] Word PDF unavailable, the server will render the document', error);
      return null;
    }
  }

  static async getDocumentAsFile(filename: string): Promise<File> {
    if (!this.isOfficeAvailable()) {
      // Create a mock file for testing when Office.js is not available
      const mockContent = 'Mock Word document content for testing';
      const blob = new Blob([mockContent], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      return new File([blob], filename, { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
    }

    try {
      return new Promise<File>((resolve, reject) => {
        // Use Office.context.document.getFileAsync to get the document as a blob
        Office.context.document.getFileAsync(
          Office.FileType.Compressed,
          { sliceSize: 65536 }, // 64KB slices
          (result: Office.AsyncResult<Office.File>) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
              reject(new Error(result.error?.message || 'Failed to get file'));
              return;
            }

            const file = result.value;
            const sliceCount = file.sliceCount;
            const slices: Uint8Array[] = [];

            // Read all slices
            let currentSlice = 0;

            const getSliceAsync = () => {
              file.getSliceAsync(currentSlice, (sliceResult: Office.AsyncResult<Office.FileSlice>) => {
                if (sliceResult.status === Office.AsyncResultStatus.Failed) {
                  file.closeAsync();
                  reject(new Error(sliceResult.error?.message || 'Failed to get slice'));
                  return;
                }

                // Store the slice data
                slices.push(new Uint8Array(sliceResult.value.data));
                currentSlice++;

                if (currentSlice < sliceCount) {
                  // Get next slice
                  getSliceAsync();
                } else {
                  // All slices retrieved, combine them
                  file.closeAsync();
                  
                  // Calculate total length
                  const totalLength = slices.reduce((acc, slice) => acc + slice.length, 0);
                  
                  // Combine all slices into a single array
                  const combined = new Uint8Array(totalLength);
                  let offset = 0;
                  
                  for (const slice of slices) {
                    combined.set(slice, offset);
                    offset += slice.length;
                  }
                  
                  const toFile = (bytes: Uint8Array) => {
                    const blob = new Blob([bytes as Uint8Array<ArrayBuffer>], {
                      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    });
                    return new File([blob], filename, {
                      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    });
                  };

                  // Word on Windows can export links to parts it left out of the file
                  // (Target="NULL"), which the server cannot open. Drop those links.
                  removeDanglingRelationships(combined)
                    .then(({ bytes, removed }) => {
                      if (removed.length > 0) {
                        console.warn('[DocumentExporter] Removed links to missing parts:', removed);
                      }
                      resolve(toFile(bytes));
                    })
                    .catch((cleanupError) => {
                      logError('Failed to clean exported document; uploading it as is', cleanupError);
                      resolve(toFile(combined));
                    });
                }
              });
            };

            // Start reading slices
            getSliceAsync();
          }
        );
      });
    } catch (error) {
      logError('Failed to export document as file', error);
      throw error;
    }
  }
}

